// Every sentence the vendor item write surfaces say (HUBX-8474).
//
// One file for all of it, including the copy that belongs to features whose rules live
// elsewhere (the conflict notice, the archive intent): a wording is the thing most likely
// to be duplicated with a one-word difference across three surfaces, and two wordings for
// one rule read to a vendor as two rules.
//
// The copy is CLIENT-owned (design doc §8.1): the server's `message`/`detail` is
// diagnostic, never shown. An unknown code falls back to the generic line rather than
// echoing server text — the catalog grows server-side and must never crash a form.

import { FormGroup, UntypedFormArray } from '@angular/forms';
import { CONFLICT_FIELDS } from './item-write-concurrency';
import { dqError, showFieldError, showGroupError } from './item-write-validators';

export const SELLER_STATUS_HINT = 'New items require HUBX review before going live';

// The three notices that say what SAVE will do beyond what the user typed (approval,
// re-review, reactivation - HUBX-8367 / HUBX-8476). Shared since HUBX-8480: the listing rows
// show them too, and the popup and the rows must not word the same consequence twice.
export const APPROVAL_NOTICE = 'This item will be approved and published when you save.';
// The re-review names no field (HUBX-8515): a cost raise sends the item back, and so does a
// Seller's change to MOQ, MXQ, ETA, Special Notes or Restrictions on an approved item.
export const REVIEW_NOTICE = 'Saving sends this item back to HUBX for review. It will not be available to buyers until it is approved.';
export const REACTIVATION_NOTICE = 'This cost brings the item back: it will be reactivated when you save.';

/** HUBX-8466: an operator's Active on an item the vendor paused approves or reactivates it,
 *  and it stays Paused (ADR-0009 Rule 6). Said before Save, in place of "published" or after
 *  the reactivation notices, and after it, in place of "Saved.". The two statuses in capitals,
 *  so they stand out in the sentence. */
export const STAYS_PAUSED = 'It will stay PAUSED. Set it to ACTIVE again to publish it.';
const STILL_PAUSED = 'Still PAUSED. Set it to ACTIVE to publish it.';
export const APPROVAL_NOTICE_STAYS_PAUSED = 'Saving will approve this item, but it will stay PAUSED. Set it to ACTIVE again to publish it.';

export function withStaysPaused(notice: string, staysPaused: boolean): string {
  return staysPaused ? `${notice} ${STAYS_PAUSED}` : notice;
}

/** After Save: what was done to the item, by the Business state it was in. */
export function savedStaysPausedNote(businessStatus: string): string {
  return `${businessStatus === 'Removed' ? 'Reactivated.' : 'Approved.'} ${STILL_PAUSED}`;
}

/** The Edit popup's success note: the status line first, then the category advisory. */
export function editSuccessNote(statusNotice?: string, categoryNotice?: string): string {
  return [statusNotice, categoryNotice].filter(Boolean).join(' ');
}

/** The fourth notice (HUBX-8476, ADR-0009 Rule 9): an operator choosing Active on a Removed
 *  item whose cost does not reach the threshold. Names the threshold when the row carries one
 *  - the same number the row and the 409 show - and leaves it out when it carries none. */
export function manualReactivationNotice(threshold: number | null | undefined): string {
  const reduceTo = threshold == null ? '' : ` (reduce to $${threshold.toFixed(2)})`;
  return `The cost is not low enough for automatic reactivation${reduceTo}. Saving will reactivate this item manually.`;
}

/** The one thing a surface says about a conflict. It cannot name the other writer:
 *  `currentState` is the read representation and carries no author and no timestamp, so
 *  this is the most the client honestly knows. "Updated since you opened it" rather than
 *  "someone else": the other writer may be this user in another tab (HUBX-8466). It does not
 *  count the fields either — the rings do that, and the resolve popup lists them. */
export const CONFLICT_NOTICE = 'This item was updated since you opened it. Your changes were not saved.';

/** The notice's one action (HUBX-8466): it opens the resolve popup. */
export const REVIEW_CHANGES = 'Review changes';

/** A Save blocked by an unresolved conflict (HUBX-8466): the item's own Save. "Review", as
 *  the notice's action and the popup's title: one verb for the one thing to do. */
export const REVIEW_FIRST = 'Review the changes first';

/** Update All Listings, blocked while any row on the page is unresolved (HUBX-8466). */
export function reviewOnItemsFirst(count: number): string {
  return `Review the changes on ${count} item${count === 1 ? '' : 's'} first`;
}

/** A Save blocked by an error the user has to fix (HUBX-8466): a field the validators refuse,
 *  or data the server refused (a 400) that has not been edited since. "Errors", not
 *  "highlighted fields": a refusal the server names no field for rings nothing. A row's own
 *  Save icon; the Edit popup's banner already says it, so its button has no tooltip. */
export const FIX_FIRST = 'Fix the errors first';

/** Update All Listings, blocked while any edited row on the page has an error to fix
 *  (HUBX-8466). Counted apart from the conflicts, which are reviewed, not fixed. */
export function fixOnItemsFirst(count: number): string {
  return `Fix the errors on ${count} item${count === 1 ? '' : 's'} first`;
}

/** The resolve popup (HUBX-8466). Its title is REVIEW_CHANGES, the action that opens it.
 *  Its one bulk action discards the edit, as Undo does; there is no "keep all mine": it
 *  would put back, in one click, the stale values of members the user never touched. */
export const DISCARD_MY_CHANGES = 'Discard my changes';
export const RESOLVE_INTRO = 'This item was updated after you opened it. Choose which value to keep.';
export const RESOLVE_LOADING = 'Loading the current version…';
export const RESOLVE_NOTHING = 'Nothing on screen differs from the current version. Apply takes the current version.';
export const RESOLVE_NOT_WRITABLE = 'You can no longer change this field.';

/** A failed re-read - Undo on a conflict, or the resolve popup's. Nothing was
 *  discarded and the conflict stays, so this is a retry prompt rather than a report of loss. */
export const RELOAD_FAILED = 'Could not load the current version. Try again.';

/** The success mark on a listing row that just saved (HUBX-8478). */
export const ROW_SAVED = 'Saved.';

/** The listing's save popup (HUBX-8478): what saved, then what did not. "Not saved" is a
 *  server failure (a 412 included) or a row the pre-flight refused; a row whose offer the
 *  user kept was held back by choice and is neither: it has its own line (HUBX-8511). The
 *  rows carry each reason, so the popup only counts and points at them. */
export const SAVE_RESULT_TITLE = { saved: 'Successful Update', partial: 'Partial Update', failed: 'Update Failed' };

/** The popup's way to the rows it reports as not saved (HUBX-8498): the rows may be off
 *  screen, so it applies the unsaved-only view the "Unsaved Listings" button applies. */
export const SHOW_UNSAVED_ACTION = 'Show Unsaved Items';

export function itemsSavedText(count: number): string {
  if (!count) { return ''; }
  return count === 1 ? '1 item was updated.' : `${count} items were updated.`;
}

export function itemsNotSavedText(count: number): string {
  if (!count) { return ''; }
  return count === 1 ? '1 item was not saved.' : `${count} items were not saved.`;
}

/** The rows the user kept back in the offers warning (HUBX-8511): not sent, still edited.
 *  Copy to confirm. */
export function itemsKeptText(count: number): string {
  if (!count) { return ''; }
  return count === 1 ? '1 item was not saved: you kept its offers.' : `${count} items were not saved: you kept their offers.`;
}

/** ProblemDetails code -> optional control to ring + the banner message. */
export const ERROR_INFO: { [code: string]: { control?: string; message: string } } = {
  DUPLICATE_VENDOR_ITEM_CODE: { control: 'vendorItemCode', message: 'A vendor item with this Part Number already exists.' },
  // No CATEGORY_MISMATCH: the server stopped emitting it when category became a per-vendor
  // facet (ADR-0007 amended, HUBX-8428) — a category differing from the shared item's is
  // allowed, and is now the non-blocking nudge plus the write-ack warning, never a failure.
  MANUFACTURER_NOT_FOUND: { control: 'manufacturerId', message: 'That manufacturer could not be found.' },
  MISSING_ITEM_KEY: { message: 'This vendor is not fully set up to create items (missing item key). Contact HUBX.' },
  RESTRICTION_ATTRIBUTE_UNAVAILABLE: { message: 'Restrictions are not available right now.' },
  CREATE_APPROVED_NOT_ALLOWED: { message: 'You are not allowed to create this item as approved.' },
  UNIT_PRICE_NOT_ALLOWED: { control: 'unitPrice', message: 'You are not allowed to set the sale price.' },
  DISCOUNT_QTYS_NOT_ALLOWED: { message: 'You are not allowed to set discount quantities.' },
  UNIT_PRICE_REQUIRED: { control: 'unitPrice', message: 'Price is required.' },
  UNIT_PURCHASE_PRICE_REQUIRED: { control: 'cost', message: 'Cost is required.' },
  UNIT_PRICE_BELOW_COST: { control: 'unitPrice', message: 'Price cannot be less than Cost.' },
  MOQ_EXCEEDS_MXQ: { control: 'mxq', message: 'MOQ cannot be greater than MXQ.' },
  DISCOUNT_QTY_BELOW_MINIMUM: { message: "Discount 'From Quantity' must be greater than 1." },
  DISCOUNT_PRICE_EXCEEDS_UNIT_PRICE: { message: 'Discount price cannot exceed or equal the price.' },
  DISCOUNT_PRICE_BELOW_COST_MARGIN: { message: 'Discount price is below the allowed cost margin.' },
  DISCOUNT_TIERS_NOT_UNIQUE: { message: 'Each discount tier must have a unique quantity and price.' },
  DISCOUNT_TIERS_NOT_DESCENDING: { message: 'A discount tier with more units must have a lower price.' },
  REQUIRED_ATTRIBUTES_MISSING: { message: 'Condition, Warranty and Packaging are required.' },
  VENDOR_CONTEXT_UNRESOLVED: { message: 'Your vendor session could not be resolved. Reload and try again.' },
  // The SellerAutomation read-only gate. The list guards this before a write surface can
  // even open, so arriving here means the server refused a request the client believed was
  // fine — a profile flag read at login and since changed, or a company that joined the
  // group mid-session. Rings nothing: the caller is the obstacle, not any value in the
  // form. Copy is legacy's verbatim, because the vendor may have seen it elsewhere in the
  // portal minutes earlier and two wordings for one rule read as two rules.
  VENDOR_PORTAL_READ_ONLY: { message: 'You are not allowed to perform this operation. Contact your account manager if you have any questions.' },
  // Target company (ADR-0010). Three distinct outcomes, three distinct messages: not
  // permitted, not a valid target, and "we could not tell" — collapsing the last into the
  // first would tell the user they lack access when the truth is a dependency was down, and
  // they'd stop retrying something that would work.
  TARGET_VENDOR_NOT_ALLOWED: { control: 'targetVendorId', message: 'You are not allowed to create items for that company.' },
  TARGET_VENDOR_NOT_ELIGIBLE: { control: 'targetVendorId', message: 'That company cannot receive new items.' },
  TARGET_VENDOR_CHECK_UNAVAILABLE: { message: 'Could not verify your access to that company right now. Try again in a moment.' },
  // The XID lock (HUBX-8458). Two outcomes kept apart for the same reason the three
  // target-company codes above are: one is terminal and one is not. Telling the user the
  // wrong one either strands a change that would have worked or invites a retry that never
  // can — SAP does not rename an item code, so the 409 does not become a 200 by waiting.
  //
  // The 409 rings the field and carries its own way out (the Revert affordance). The 503
  // rings nothing: no fault was found with the value, the check simply did not run, and a
  // ring would say the opposite. Its retry is the Save button on a form left intact — the
  // same affordance TARGET_VENDOR_CHECK_UNAVAILABLE has always relied on.
  HUBX_ITEM_CODE_LOCKED_IN_B1: { control: 'hubxItemCode', message: 'This item already exists in SAP, so its XID can no longer be changed.' },
  HUBX_ITEM_CODE_CHECK_UNAVAILABLE: { message: 'Could not check this item against SAP right now, so the XID was left unchanged. Try again in a moment.' },
  // A Removed item whose cost does not reach the reactivation threshold (ADR-0009 Rule 9).
  // Rings Cost: the cost is what has to change, and a retry as-is fails the same way. This
  // static line is for a 409 without figures (no band covers the stored cost); with them,
  // errorFor names the threshold.
  REACTIVATION_THRESHOLD_NOT_MET: { control: 'cost', message: 'This cost is not low enough to reactivate this item.' },
  // A HUBX-owned item (HUBX-8466): SAP owns everything but its status, archive and discount
  // quantities, and those are the only controls writableFields leaves enabled. TERMINAL, like
  // the XID lock - the value has to go back, a retry as-is fails the same way. This static line
  // is for a 409 whose data.field names nothing the form has; errorFor names the field.
  ITEM_NOT_PORTAL_EDITABLE: { message: "This item is managed in SAP, so this change can't be made from the portal." }
  // No CONCURRENCY_CONFLICT entry: the 412 is handled before this table is consulted,
  // because it owns the amber band and Review changes rather than the red error line. A
  // message here would render a second one beside it.
};

/** The catalogue entry for a ProblemDetails code. `data` is the problem's `data` member. The
 *  codes whose line needs figures use the ones the server sent, and keep their static line when
 *  none came (or not all of them):
 *  - REACTIVATION_THRESHOLD_NOT_MET names the threshold - the same number the listing row
 *    shows (HUBX-8494).
 *  - DISCOUNT_PRICE_BELOW_COST_MARGIN names the first failing tier and the floor (HUBX-8503),
 *    in legacy's words (item.service.ts validateForm, with 'cannot' as the other v2 lines
 *    spell it), so a vendor who knew the rule reads the same rule. The rule itself stays server-side: whether the item is third-party and
 *    the margin % are the server's to know.
 *  - ITEM_NOT_PORTAL_EDITABLE names the field `data.field` points at, and rings it.
 *  `dqFromQty` (the cost-margin error, with its figures) names the failing discount tier by its
 *  From Quantity, unique on an item: legacy rang that tier, and a tier is not one control. */
export function errorFor(code?: string, data?: { [key: string]: string }): { control?: string; message: string; dqFromQty?: number } | undefined {
  const info = code ? ERROR_INFO[code] : undefined;
  if (info && code === 'ITEM_NOT_PORTAL_EDITABLE') {
    const field = fieldNamed(data?.['field']);
    if (field?.control) {
      return { control: field.control, message: `This item is managed in SAP, so '${field.label}' can't be changed from the portal.` };
    }
  }
  if (info && code === 'DISCOUNT_PRICE_BELOW_COST_MARGIN') {
    const [fromQty, price, floor, margin] = ['fromQty', 'price', 'costWithMargin', 'marginPercent']
      .map(key => data?.[key] ? Number(data[key]) : NaN);
    if ([fromQty, price, floor, margin].every(Number.isFinite)) {
      return {
        ...info,
        message: `Discount price (${fromQty} \u2192 $${price.toFixed(2)}) cannot be less than cost with ${margin}% margin ($${floor.toFixed(2)}).`,
        dqFromQty: fromQty
      };
    }
  }
  if (info && code === 'REACTIVATION_THRESHOLD_NOT_MET') {
    const raw = data?.['reactivationThreshold'];
    const threshold = raw ? Number(raw) : NaN;
    if (Number.isFinite(threshold)) {
      return { ...info, message: `To reactivate this item, the cost must be $${threshold.toFixed(2)} or less.` };
    }
  }
  return info;
}

/** The member a ProblemDetails `data.field` names. The server uses the request's name where it
 *  differs from the aggregate's (`availability`, `status`, `mpn`, `manufacturer`) and the
 *  aggregate's member name otherwise (`Description`, `LeadTimeDays`, `Prices`), so the match
 *  ignores case and maps the three names that are not the same word as the key. */
const FIELD_ALIASES: { [field: string]: string } = {
  status: 'effectiveStatus',
  manufacturer: 'manufacturerId',
  prices: 'discountQtys'
};

function fieldNamed(field?: string) {
  if (!field) { return undefined; }
  const name = field.toLowerCase();
  const key = (FIELD_ALIASES[name] ?? name).toLowerCase();
  return CONFLICT_FIELDS.find(f => f.key.toLowerCase() === key);
}

/** The fallback when the code is unknown, unmapped, or absent. */
export function genericWriteError(isEdit: boolean): string {
  return isEdit
    ? 'Could not save the item. Please try again.'
    : 'Could not create the item. Please try again.';
}

/** The discount-tier rules (dqTierValidator), in the order the modal reports them. */
const DQ_TIER_RULES = [
  'incompletePair', 'fromQtyBelowMin', 'fromQtyNotUnique', 'priceExceedsUnit', 'priceBelowCostMargin', 'priceNotUnique',
  'priceNotDescending'
] as const;

/** The figures dqTierValidator puts on the margin-floor error (HUBX-8466). */
interface IDqMarginFigures { fromQty: number; price: number; floor: number; margin: number; }

/** What a tier error carries for its line (HUBX-8466): the margin floor's figures, or the
 *  0-based index of the other tier: the earlier one a repeat repeats, or the smaller one a
 *  larger tier is priced above. */
type DqDetail = IDqMarginFigures | { other: number };

/** The rules whose line names something of the tier itself, so each tier gets its own sentence. */
const DETAILED_DQ_RULES = new Set<string>(['priceBelowCostMargin', 'fromQtyNotUnique', 'priceNotUnique', 'priceNotDescending']);

function otherTier(detail?: DqDetail): string {
  return detail && 'other' in detail ? `DQ #${detail.other + 1}` : 'the other tiers';
}

/** One tier rule, worded once for the modal's banner and the listing rows, for every tier that
 *  breaks it. `indexes` are 0-based; the user reads DQ #1-#3. */
function dqTierMessage(indexes: readonly number[], rule: string, detail?: DqDetail): string | undefined {
  const figures = detail && 'floor' in detail ? detail : undefined;
  const names = indexes.map(i => `DQ #${i + 1}`);
  const tier = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  switch (rule) {
    case 'incompletePair': return `${tier}: 'From Quantity' and 'Price' are both required to set up a discount.`;
    case 'fromQtyBelowMin': return `${tier}: 'From Quantity' needs to be greater than 1.`;
    case 'priceExceedsUnit': return `${tier}: Discount price cannot exceed or equal the price.`;
    // Legacy's line with the tier named (HUBX-8466); without the figures (the listing line,
    // which reads error names only), the rule alone.
    case 'priceBelowCostMargin': return figures
      ? `${tier}: Discount price (${figures.fromQty} \u2192 $${figures.price.toFixed(2)}) cannot be less than cost with ${figures.margin}% margin ($${figures.floor.toFixed(2)}).`
      : `${tier}: Discount price cannot be less than cost plus the discount margin.`;
    // A repeat names the tier it repeats (HUBX-8466); by name only, the rule.
    case 'fromQtyNotUnique': return `${tier}: 'From Quantity' must be different from ${otherTier(detail)}.`;
    case 'priceNotUnique': return `${tier}: Price must be different from ${otherTier(detail)}.`;
    // More units, lower price (HUBX-8466): names the smaller tier it is priced above.
    case 'priceNotDescending': return detail && 'other' in detail
      ? `${tier}: Price must be lower than DQ #${detail.other + 1}, which has a smaller quantity.`
      : `${tier}: Price must be lower than the tiers with a smaller quantity.`;
    default: return undefined;
  }
}

/** Every broken tier, each with its first rule: one sentence per rule, naming all the tiers that
 *  break it, in the order the tiers come. Naming only the first broken tier left a user who moved
 *  on to DQ #2 reading about DQ #1. */
function dqTierMessages(broken: readonly { index: number; rule: string; detail?: DqDetail }[]): string[] {
  const rules = [...new Set(broken.map(b => b.rule))];
  return rules
    .reduce((out: (string | undefined)[], rule) => {
      const tiers = broken.filter(b => b.rule === rule);
      // A tier under the margin floor, or repeating another, has its own figures or its own
      // other tier, so each gets its own sentence.
      return out.concat(DETAILED_DQ_RULES.has(rule) && tiers.every(b => b.detail)
        ? tiers.map(b => dqTierMessage([b.index], rule, b.detail))
        : [dqTierMessage(tiers.map(b => b.index), rule)]);
    }, [])
    .filter((m): m is string => !!m);
}

/** Single consolidated message for the footer banner (mirroring legacy's
 *  item.service.validateForm): the specific cross-field / discount messages come first, and
 *  a generic pointer to the highlighted fields covers the remaining required / format /
 *  positivity failures. The per-field red rings show WHERE; this line says WHAT.
 *
 *  `submitted` gates the generic line only. The modal passes it once its Save waits on a shown
 *  error as well (HUBX-8466): a blocked Save always says why, so the line comes as soon as a
 *  field is left refused. The rows pass false, and say it their own way (rowInvalidMessage). */
export function bannerError(
  fg: FormGroup,
  dqControls: UntypedFormArray,
  serverError: string | null,
  submitted: boolean
): string | null {
  if (serverError) {
    return serverError;
  }
  if (showGroupError(fg, 'unitPriceBelowCost', 'unitPrice', 'cost')) {
    return 'Price cannot be less than Cost.';
  }
  if (showGroupError(fg, 'moqExceedsMxq', 'moq', 'mxq')) {
    return 'MOQ cannot be greater than MXQ.';
  }
  const broken: { index: number; rule: string; detail?: DqDetail }[] = [];
  for (let i = 0; i < dqControls.length; i++) {
    const rule = DQ_TIER_RULES.find(r => dqError(dqControls, i, r));
    if (rule) {
      const detail = DETAILED_DQ_RULES.has(rule) ? dqControls.at(i).getError(rule) as DqDetail : undefined;
      broken.push({ index: i, rule, detail });
    }
  }
  if (broken.length) {
    return dqTierMessages(broken).join(' ');
  }
  for (const name of Object.keys(fg.controls)) {
    const tooLong = fg.controls[name].errors?.['maxlength'];
    const message = tooLong && showFieldError(fg, name) ? maxLengthMessage(name, tooLong.requiredLength) : undefined;
    if (message) {
      return message;
    }
  }
  if (fg.invalid && submitted) {
    return 'Please complete the highlighted fields.';
  }
  return null;
}

/** The cross-field rules, worded as the modal's banner words them. */
const LISTING_GROUP_ERRORS: { [error: string]: string } = {
  unitPriceBelowCost: 'Price cannot be less than Cost.',
  moqExceedsMxq: 'MOQ cannot be greater than MXQ.'
};

/** The form controls the shared conflict map does not label, by the label the rows use. */
const LISTING_EXTRA_LABELS: { [control: string]: string } = {
  vendorItemCode: 'Part Number',
  categoryMainId: 'Category',
  unitPrice: 'Price'
};

function listingFieldLabel(control: string): string | undefined {
  return LISTING_EXTRA_LABELS[control] ?? CONFLICT_FIELDS.find(f => f.control === control)?.label;
}

/** What a refused form reports: its invalid controls (a disabled one is never invalid),
 *  then the form's own cross-field errors. The names listingInvalidMessage reads.
 *
 *  A control over its length limit is reported as `<control>.maxlength.<limit>`.
 *
 *  The discount tiers are reported as `discountQtys.<index>.<rule>` - every broken tier
 *  with its first rule, in the modal's order - so the row can say what the modal says rather
 *  than "Discount quantities". The bare `discountQtys` stays for a list invalid for any other
 *  reason. */
export function formErrorNames(fg: FormGroup): string[] {
  return [
    ...Object.keys(fg.controls)
      .filter(name => fg.controls[name].invalid)
      .reduce((names: string[], name) => names.concat(
        name === 'discountQtys' ? dqTierErrorNames(fg.controls[name] as UntypedFormArray) : [controlErrorName(fg, name)]), []),
    ...Object.keys(fg.errors ?? {})
  ];
}

/** A control over its length limit is reported as `<control>.maxlength.<limit>`, so the row can
 *  name the limit instead of asking to complete a field that is full. */
function controlErrorName(fg: FormGroup, name: string): string {
  const tooLong = fg.controls[name].errors?.['maxlength'];
  return tooLong ? `${name}.maxlength.${tooLong.requiredLength}` : name;
}

/** The line for a field longer than its limit, by the label the user sees. */
function maxLengthMessage(control: string, limit: number): string | undefined {
  const label = listingFieldLabel(control);
  return label ? `${label} cannot be longer than ${limit} characters.` : undefined;
}

const MAX_LENGTH_ERROR = /^(\w+)\.maxlength\.(\d+)$/;

function dqTierErrorNames(tiers: UntypedFormArray): string[] {
  const names: string[] = [];
  for (let i = 0; i < tiers.length; i++) {
    const rule = DQ_TIER_RULES.find(r => tiers.at(i).hasError(r));
    if (rule) {
      names.push(`discountQtys.${i}.${rule}`);
    }
  }
  return names.length ? names : ['discountQtys'];
}

const DQ_TIER_ERROR = /^discountQtys\.(\d+)\.(\w+)$/;

/** The sentence for a cross-field, tier or length rule, or undefined for a field to complete. */
function ruleMessage(error: string): string | undefined {
  const tier = DQ_TIER_ERROR.exec(error);
  if (tier) {
    return dqTierMessage([Number(tier[1])], tier[2]);
  }
  const tooLong = MAX_LENGTH_ERROR.exec(error);
  return tooLong ? maxLengthMessage(tooLong[1], Number(tooLong[2])) : LISTING_GROUP_ERRORS[error];
}

/** The line a listing row shows when its form is refused (HUBX-8479) - live under the row,
 *  and from the save pre-flight: the errors are formErrorNames.
 *  Unlike the modal, the rows draw no per-field ring (only the price and MOQ inputs turn
 *  red, for the cross-field rules), so "the highlighted fields" would point at nothing: the
 *  rule is stated, and the fields to complete are named, each once, in the order reported. */
export function listingInvalidMessage(errors: readonly string[]): string {
  return rowInvalidMessage(errors, new Set());
}

const CATEGORY_CONTROLS = ['categoryMainId', 'categoryId'];

/** The line under a row whose form is refused (HUBX-8479), as the Edit modal says it where
 *  the row can: `visible` is what this view draws, and each of those is ringed, so the line
 *  points at "the highlighted fields"; a refused field the view does not draw (the simple row
 *  has no attribute band, neither row has MPN) cannot be ringed and is named instead. The
 *  category leaf counts as drawn whenever either category select is - with no sub list, the
 *  main select carries its ring. Cross-field and discount-tier rules are stated first, as in
 *  the banner, and in its words. */
export function rowInvalidMessage(errors: readonly string[], visible: ReadonlySet<string>, dqControls?: UntypedFormArray): string {
  // The tier errors are stated together where the first one stands, so the tiers breaking the
  // same rule share one sentence, as in the banner.
  const tiers = errors.map(e => DQ_TIER_ERROR.exec(e)).filter((m): m is RegExpExecArray => !!m)
    .map(m => ({
      index: Number(m[1]),
      rule: m[2],
      // The margin floor's figures, or the tier a repeat repeats (HUBX-8466), when the
      // caller handed its tiers over.
      detail: DETAILED_DQ_RULES.has(m[2])
        ? dqControls?.at(Number(m[1]))?.getError(m[2]) as DqDetail | undefined
        : undefined
    }));
  const firstTier = errors.findIndex(e => DQ_TIER_ERROR.test(e));
  const rules = errors
    .reduce((out: (string | undefined)[], e, i) => DQ_TIER_ERROR.test(e)
      ? (i === firstTier ? out.concat(dqTierMessages(tiers)) : out)
      : out.concat(ruleMessage(e)), [])
    .filter((m): m is string => !!m);
  const controls = errors.filter(e => !ruleMessage(e) && !!listingFieldLabel(e));
  const drawn = (c: string) => visible.has(c)
    || (CATEGORY_CONTROLS.includes(c) && CATEGORY_CONTROLS.some(x => visible.has(x)));
  const highlighted = controls.some(drawn);
  const hidden = [...new Set(controls.filter(c => !drawn(c)).map(c => listingFieldLabel(c) as string))];

  const parts = [...rules];
  if (highlighted && hidden.length) {
    parts.push(`Please complete the highlighted fields and: ${hidden.join(', ')}.`);
  } else if (highlighted) {
    parts.push('Please complete the highlighted fields.');
  } else if (hidden.length) {
    parts.push(`Please complete: ${hidden.join(', ')}.`);
  }
  return parts.length ? parts.join(' ') : 'Please review this item before saving.';
}

/** Whether the archived state differs from what was read — what the intent bar keys off.
 *  Null on a surface with nothing read yet (a Create), where there is no intent to state. */
export function archiveIntentChanged(isArchived: boolean, baseline?: { isArchived: boolean }): boolean {
  return !!baseline && isArchived !== baseline.isArchived;
}

/** What the pending archive intent will do on save. Null when nothing is pending, which is
 *  what hides the bar. */
export function archiveIntentNotice(isArchived: boolean, baseline?: { isArchived: boolean }): string | null {
  if (!archiveIntentChanged(isArchived, baseline)) {
    return null;
  }
  return isArchived
    ? 'This item will be archived when you save.'
    : 'This item will be restored when you save.';
}
